import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, downloadMaterialsTemplate, importMaterials, downloadMaterials } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Package, CaretRight, Warning, DownloadSimple, UploadSimple, MagnifyingGlass } from "@phosphor-icons/react";

const empty = { name: "", category: "", specification: "", unit: "pcs", min_stock: 0, initial_stock: 0 };

function StatusBadge({ status }) {
  const map = {
    healthy: "bg-[#16A34A]",
    low: "bg-[#EAB308]",
    out: "bg-[#DC2626]",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase text-white ${map[status]}`}>
      {status !== "healthy" && <Warning size={11} weight="bold" />}
      {status}
    </span>
  );
}

export default function Materials() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("materials", "edit");
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const load = () => api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? materials.filter((m) => [m.name, m.category, m.specification].some((f) => (f || "").toLowerCase().includes(q)))
    : materials;

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const data = await importMaterials(file);
      setResult(data);
      if (data.created > 0) toast.success(`Imported ${data.created} material${data.created === 1 ? "" : "s"}`);
      else if (data.skipped > 0) toast.error("Nothing imported — see details");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const onTemplate = async () => {
    try { await downloadMaterialsTemplate(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Material name is required");
    setSaving(true);
    try {
      await api.post("/materials", {
        ...form,
        min_stock: Number(form.min_stock) || 0,
        initial_stock: Number(form.initial_stock) || 0,
      });
      toast.success("Material added");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title="Raw Materials" subtitle="Inventory Catalogue">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => downloadMaterials("xlsx").catch((err) => toast.error(apiError(err.response?.data?.detail)))}
            variant="outline"
            data-testid="export-materials-xlsx"
            className="rounded-none border-zinc-300 font-bold uppercase tracking-wider transition-colors duration-100"
          >
            <DownloadSimple size={16} weight="bold" className="mr-1" /> Excel
          </Button>
          <Button
            onClick={() => downloadMaterials("pdf").catch((err) => toast.error(apiError(err.response?.data?.detail)))}
            variant="outline"
            data-testid="export-materials-pdf"
            className="rounded-none border-zinc-300 font-bold uppercase tracking-wider transition-colors duration-100"
          >
            <DownloadSimple size={16} weight="bold" className="mr-1" /> PDF
          </Button>
          {canEdit && (
          <>
          <input ref={fileRef} type="file" accept=".xlsx" onChange={onImportFile} className="hidden" data-testid="import-materials-input" />
          <Button
            onClick={onTemplate}
            variant="outline"
            data-testid="download-template-button"
            className="rounded-none border-zinc-300 font-bold uppercase tracking-wider transition-colors duration-100"
          >
            <DownloadSimple size={16} weight="bold" className="mr-1" /> Template
          </Button>
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            variant="outline"
            data-testid="import-materials-button"
            className="rounded-none border-zinc-300 font-bold uppercase tracking-wider transition-colors duration-100"
          >
            <UploadSimple size={16} weight="bold" className="mr-1" /> {importing ? "Importing…" : "Import Excel"}
          </Button>
          <Button
            onClick={() => setOpen(true)}
            data-testid="add-material-button"
            className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider transition-colors duration-100"
          >
            <Plus size={16} weight="bold" className="mr-1" /> Add Material
          </Button>
          </>
          )}
        </div>
      </PageHeader>

      <div className="p-4 sm:p-8">
        <div className="relative mb-4 max-w-md">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="materials-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, category, or specification…"
            className="rounded-none border-zinc-300 pl-9 focus-visible:ring-2 focus-visible:ring-primary"
          />
        </div>
        {/* Desktop: table */}
        <div className="hidden sm:block bg-white border border-zinc-300 overflow-x-auto">
          <table className="w-full text-sm" data-testid="materials-table">
            <thead>
              <tr className="border-b border-zinc-300 bg-zinc-50 text-left">
                <th className="px-6 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Material</th>
                <th className="px-6 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Category</th>
                <th className="px-6 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">Total</th>
                <th className="px-6 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">Blocked</th>
                <th className="px-6 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">Available</th>
                <th className="px-6 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">Min</th>
                <th className="px-6 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-zinc-400">
                  <Package size={32} weight="thin" className="mx-auto mb-3" />
                  {q ? "No materials match your search." : "No raw materials yet. Add your first one."}
                </td></tr>
              )}
              {filtered.map((m) => (
                <tr
                  key={m.id}
                  onClick={() => navigate(`/materials/${m.id}`)}
                  data-testid={`material-row-${m.id}`}
                  className="cursor-pointer hover:bg-zinc-50 transition-colors duration-100"
                >
                  <td className="px-6 py-4">
                    <p className="font-medium text-zinc-900">{m.name}</p>
                    {m.specification && <p className="text-xs text-zinc-400 mt-0.5 line-clamp-1">{m.specification}</p>}
                  </td>
                  <td className="px-6 py-4 text-zinc-600">{m.category || "—"}</td>
                  <td className="px-6 py-4 text-right font-mono tabular font-medium">{m.current_stock} <span className="text-zinc-400">{m.unit}</span></td>
                  <td className="px-6 py-4 text-right font-mono tabular text-[#EA580C]">{m.reserved > 0 ? m.reserved : "—"}</td>
                  <td className="px-6 py-4 text-right font-mono tabular font-bold text-zinc-900">{m.available}</td>
                  <td className="px-6 py-4 text-right font-mono tabular text-zinc-500">{m.min_stock}</td>
                  <td className="px-6 py-4"><StatusBadge status={m.status} /></td>
                  <td className="px-6 py-4 text-right"><CaretRight size={16} className="text-zinc-400 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked cards */}
        <div className="sm:hidden space-y-3" data-testid="materials-cards">
          {filtered.length === 0 && (
            <div className="bg-white border border-zinc-300 px-4 py-12 text-center text-zinc-400">
              <Package size={32} weight="thin" className="mx-auto mb-3" />
              {q ? "No materials match your search." : "No raw materials yet. Add your first one."}
            </div>
          )}
          {filtered.map((m) => (
            <button
              key={m.id}
              onClick={() => navigate(`/materials/${m.id}`)}
              data-testid={`material-card-${m.id}`}
              className="w-full text-left bg-white border border-zinc-300 p-4 active:bg-zinc-50 transition-colors duration-100"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-zinc-900 break-words">{m.name}</p>
                  {m.specification && <p className="text-xs text-zinc-400 mt-0.5">{m.specification}</p>}
                  {m.category && <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mt-1">{m.category}</p>}
                </div>
                <StatusBadge status={m.status} />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-zinc-100">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Available</p>
                  <p className="font-mono tabular font-bold text-zinc-900">{m.available} <span className="text-zinc-400 font-normal">{m.unit}</span></p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Blocked</p>
                  <p className="font-mono tabular text-[#EA580C]">{m.reserved > 0 ? m.reserved : "—"}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Total / Min</p>
                  <p className="font-mono tabular text-zinc-500">{m.current_stock} / {m.min_stock}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-lg" data-testid="material-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">New Raw Material</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Name *</Label>
              <Input data-testid="material-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Steel Sheet 0.8mm" className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Category</Label>
                <Input data-testid="material-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Metal / Electrical" className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Unit</Label>
                <Input data-testid="material-unit-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="pcs / kg / m" className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider">Specification</Label>
              <Textarea data-testid="material-spec-input" value={form.specification} onChange={(e) => setForm({ ...form, specification: e.target.value })} placeholder="Grade, dimensions, tolerance, supplier notes…" className="rounded-none border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Initial Stock</Label>
                <Input type="number" data-testid="material-initial-input" value={form.initial_stock} onChange={(e) => setForm({ ...form, initial_stock: e.target.value })} className="rounded-none border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Min Threshold</Label>
                <Input type="number" data-testid="material-min-input" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="rounded-none border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none border-zinc-300">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="material-save-button" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">{saving ? "Saving…" : "Save Material"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="rounded-none border-zinc-300 sm:max-w-lg" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Import Complete</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-4">
              <div className="flex-1 border border-zinc-300 p-4">
                <p className="text-3xl font-mono font-bold text-[#16A34A]" data-testid="import-created-count">{result?.created ?? 0}</p>
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mt-1">Added</p>
              </div>
              <div className="flex-1 border border-zinc-300 p-4">
                <p className="text-3xl font-mono font-bold text-[#EAB308]">{result?.skipped ?? 0}</p>
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mt-1">Skipped</p>
              </div>
            </div>
            {result?.errors?.length > 0 && (
              <div className="border border-zinc-300 max-h-52 overflow-y-auto" data-testid="import-errors">
                {result.errors.map((e, i) => (
                  <p key={`${i}-${e}`} className="px-3 py-2 text-sm text-zinc-600 border-b border-zinc-100 last:border-0 flex items-start gap-2">
                    <Warning size={14} weight="bold" className="text-[#EAB308] mt-0.5 shrink-0" /> {e}
                  </p>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setResult(null)} data-testid="import-result-close" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
